/**
 * "Export CSV" for a list table: downloads every row matching the table's current search, filters
 * and sort — not just the rows loaded so far (lib/tableExport).
 *
 * - A table the API returns whole passes `rows`: what its filters and sort left, all of them.
 * - A server-paged table passes `fetchPage`, a loader for one page of its own query; the rows are
 *   collected page by page, with progress on the button and a Cancel.
 *
 * The query is the one in effect when the button is pressed; changing filters meanwhile doesn't
 * change a running export.
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { describeError } from "../../api/errors";
import {
  collectAll,
  EXPORT_MAX_ROWS,
  ExportCancelled,
  exportFileName,
  saveTextFile,
  toCsv,
  type ExportColumn,
  type PageOf,
} from "../../lib/tableExport";

const WHOLE = new Intl.NumberFormat("en-IN");

export function ExportButton<T>({
  table,
  columns,
  rows,
  fetchPage,
  keyOf,
  disabled = false,
  label = "Export CSV",
}: {
  /** What is exported, for the file name and messages ("medicines" → medicines-2026-10-19-1405.csv). */
  table: string;
  columns: readonly ExportColumn<T>[];
  /** Every matching row, when the table has them all. */
  rows?: readonly T[];
  /** One page of the table's current query, when the server pages it. */
  fetchPage?: (next: string | number | undefined) => Promise<PageOf<T>>;
  /** A row's identity, so a row met twice while paging (the list moved) is exported once. */
  keyOf?: (row: T) => string;
  disabled?: boolean;
  label?: string;
}) {
  const [progress, setProgress] = useState<{ loaded: number; total?: number } | null>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  const save = (data: readonly T[], truncated: boolean) => {
    if (data.length === 0) {
      toast.info(`No ${table} match the current filters, so there was nothing to export.`);
      return;
    }
    saveTextFile(toCsv(data, columns), exportFileName(table));
    if (truncated) {
      toast.warning(
        `Exported the first ${WHOLE.format(EXPORT_MAX_ROWS)} ${table}. Narrow the filters (e.g. a date range) to export the rest.`,
      );
    } else {
      toast.success(`Exported ${WHOLE.format(data.length)} ${table}.`);
    }
  };

  const run = async () => {
    if (rows) return save(rows, false);
    if (!fetchPage) return;
    const controller = new AbortController();
    abort.current = controller;
    setProgress({ loaded: 0 });
    try {
      const result = await collectAll(fetchPage, {
        signal: controller.signal,
        keyOf,
        onProgress: (loaded, total) => setProgress({ loaded, total }),
      });
      save(result.rows, result.truncated);
    } catch (e) {
      if (e instanceof ExportCancelled) toast.info("Export cancelled.");
      else toast.error(`Export failed: ${describeError(e)}`);
    } finally {
      if (abort.current === controller) abort.current = null;
      setProgress(null);
    }
  };

  const running = progress !== null;
  const status = running
    ? `Exporting ${WHOLE.format(progress.loaded)}${progress.total !== undefined ? ` of ${WHOLE.format(Math.min(progress.total, EXPORT_MAX_ROWS))}` : ""}…`
    : "";

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      <button
        type="button"
        onClick={() => void run()}
        disabled={disabled || running}
        aria-busy={running || undefined}
        title={`Download every ${table} row matching the current filters`}
        className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-xs transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-60"
      >
        <DownloadIcon spinning={running} />
        <span className={running ? "" : "hidden sm:inline"}>{running ? status : label}</span>
        {!running && <span className="sm:hidden">Export</span>}
      </button>
      {running && (
        <button
          type="button"
          onClick={() => abort.current?.abort()}
          className="rounded-lg px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          Cancel
        </button>
      )}
      <span className="sr-only" aria-live="polite">
        {status}
      </span>
    </span>
  );
}

function DownloadIcon({ spinning }: { spinning: boolean }) {
  if (spinning) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="h-4 w-4 animate-spin" aria-hidden>
        <path d="M21 12a9 9 0 1 1-6.2-8.56" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
      <path d="M12 4v11m0 0-4-4m4 4 4-4" />
      <path d="M5 19h14" />
    </svg>
  );
}
